/*
 * File: base-classes.ts
 * File Created: 2023-12-19 11:33:06
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2026-01-15 12:16:34
 * Modified By: Val Liu <valuis0429@gmail.com>
 * -----
 */

import { PropInnateSync } from "@root/core/vue-props";
import {
    Vue,
    Component,
    Provide,
    Prop,
    Model,
    PropSync,
    Watch,
    ModelSync,
    ProvideReactive,
} from "vue-property-decorator";
import { IFormState } from "./form/core/lib/core";

/// auto-completion helper
type SlotKey = string & { _slotKeyBrand?: undefined };

/// variants
export const Variants = [
    /// normal
    "primary",
    "secondary",
    "success",
    "info",
    "warning",
    "danger",
    "light",
    "dark",

    /// outline
    "outline-primary",
    "outline-secondary",
    "outline-success",
    "outline-info",
    "outline-warning",
    "outline-danger",
    "outline-light",
    "outline-dark",

    /// custom style
    "transparent",
] as const;
export type VariantType = (typeof Variants)[number];

const sizes = ["xl", "lg", "md", "sm"] as const;
type SizeType = (typeof sizes)[number];

@Component
export class BaseComponent extends Vue {
    /// base id for each component
    _uid!: number;

    /// The main text appears in front of the component.
    @Prop({ type: String, required: false })
    label: string | undefined;

    /// Bootstrap variant. primary / secondary / success / info / warning / danger / light / dark.
    @Prop({
        default: "primary",
        validator: (value: string) => {
            const inc = Variants.includes(value as any);
            if (inc == false)
                console.error(`<${value}> is not a valid value for <variant>.`);
            return inc;
        },
    })
    variant!: VariantType;

    /// Bootstrap size. xl / lg / md / sm.
    @Prop({ default: "md", validator: (value: string) => sizes.includes(value as any) })
    size!: SizeType;

    /// the disabled state
    @Prop({ type: Boolean, required: false, default: false })
    disabled!: boolean;

    /// The main source as “value”, and emit as “input”.
    /// Suggest to overwrite it on every component to enhance type safety.
    /// Must use @PropInnateSync in a form component.
    @Prop({ required: false })
    value: any;
    // value: any = null;
    // @PropInnateSync("value", { required: false })
    // innateValue!: any;

    /// Every other attributes should be put right here.
    /// Overwrite if necessary.
    @Prop({ type: Object, required: false, default: () => ({}) })
    data: any;

    /// named predefined slots should define here
    publicSlots?: Record<string, any>;

    /// slot helpers, features:
    /// 1) pick all $slots, $scopedSlots
    /// 2) remove publicSlots
    /// 3) remove other slots
    /// 4) pick slots
    omitSlots(slots, ...keys: Array<keyof this["publicSlots"] | SlotKey>) {
        return this.filterSlots(slots, "omit", keys);
    }
    pickSlots(slots, ...keys: Array<keyof this["publicSlots"] | SlotKey>) {
        return this.filterSlots(slots, "pick", keys);
    }

    filterSlots(
        slots,
        type: "omit" | "pick",
        keys?: Array<keyof this["publicSlots"] | SlotKey>,
    ) {
        /// default to predefined slots
        keys = keys ?? [];
        /// mapping the publicSlots if exists
        keys = keys.map((key) => {
            const slotValue = ((this.publicSlots as any) ?? {})[key];
            return slotValue ?? key;
        });
        /// if type omit, defaulty remove the publicSlots
        if (type === "omit") keys = [...keys, ...Object.values(this.publicSlots ?? {})];
        return Object.keys(slots)
            .filter((key: any) => {
                if (keys == undefined) return false;
                if (type === "omit") return keys.indexOf(key) < 0;
                if (type === "pick") return keys.indexOf(key) >= 0;
            })
            .reduce((final, key) => {
                final[key] = slots[key];
                return final;
            }, {});
    }

    /// make use of general global object
    get window() {
        return window;
    }
    get console() {
        return console;
    }
}

/// todo: move to other place
// base class for all `View`.
@Component
export class BaseView extends Vue {
    @Provide()
    viewInstance = this;

    /// defaulty provide the overwritable prefix
    static prefix = "";
    @Provide()
    prefix = BaseView.prefix;

    /// make use of general global object
    get window() {
        return window;
    }
    get console() {
        return console;
    }

    //  ref CRUD
    ref(type: string, key?: any) {
        const id = `ref-${type}-${(this as any)._uid}-${key}`;
        let ref = this.$refs[id];
        if (Array.isArray(ref)) ref = ref[0];
        const result = { id, ref };
        return result;
    }
}

// base class for all `Auto` components.
@Component
export class MixinAuto extends BaseComponent {
    @ProvideReactive()
    autoInstance: MixinAuto | undefined;

    get autoComponentId() {
        return `auto-component-${this._uid}`;
    }

    /// private helper
    private created() {
        this.autoInstance = this;
    }
}
